import { ReactNode, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ExpandedState, Row } from '@tanstack/react-table';

import {
  buildReferralRows,
  buildReferralRowsWithSubRows,
  ReferralColumnKey,
  ReferralTableRow,
} from 'components/insights/careTransitions/referrals/table/referralDetailsColumns';
import ReferralDetailsTable from 'components/insights/careTransitions/referrals/table/ReferralDetailsTable';
import { Dimensions } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import SegmentedControl from 'components/shared/SegmentedControl';
import { useReferralsInsightsActions, useReferralsInsightsStore } from 'stores/referralsInsightsStore';
import { colors } from 'styles/theme/colors';

import { Chevron, ExpandButton, LeafName, SubRowName, TableWrapper } from '../../shared/slideout/expandableRowStyles';
import { getEntityColumnHeader, getSlideoutViewByOptions, SUB_DIVISIBLE_DIMENSIONS } from '../constants';
import { useReferralsInsightsContext } from '../ReferralsContainer';

const getSubRowsAccessor = (row: ReferralTableRow) => row.subRows;

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  viewByOptions?: Dimensions[];
  defaultSort?: { id: string; desc: boolean };
  exportSlot?: ReactNode;
  visibleColumns?: readonly ReferralColumnKey[];
};

export default function ReferralInsightsTable({
  loading,
  rollups,
  viewByOptions,
  defaultSort,
  exportSlot,
  visibleColumns,
}: Props) {
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const slideoutDimension = useReferralsInsightsStore((state) => state.slideoutDimension);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { setSlideoutDimension } = useReferralsInsightsActions();

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { sendingGroupType } = useReferralsInsightsContext();
  const sendingGroupTypeName = sendingGroupType?.displayName ?? '';
  const receivingGroupTypeName = selectedReceivingGroupType?.displayName ?? '';
  const headerNames = useMemo(
    () => ({ sendingGroupTypeName, receivingGroupTypeName }),
    [sendingGroupTypeName, receivingGroupTypeName]
  );

  const segmentedOptions = useMemo(() => {
    let dims: Dimensions[] = [];
    if (viewByOptions) {
      dims = viewByOptions;
    } else if (slideoutContext?.kind === 'entity') {
      dims = getSlideoutViewByOptions(slideoutContext.dimension);
    }
    return dims.map((d) => ({ label: getEntityColumnHeader(d, headerNames), value: d }));
  }, [slideoutContext, headerNames, viewByOptions]);

  const isSubDivisible = slideoutDimension != null && SUB_DIVISIBLE_DIMENSIONS.has(slideoutDimension);

  const tableRows = useMemo(
    () =>
      isSubDivisible
        ? buildReferralRowsWithSubRows(rollups, slideoutDimension ?? undefined)
        : buildReferralRows(rollups, slideoutDimension ?? undefined),
    [rollups, isSubDivisible, slideoutDimension]
  );

  const entityCell = useCallback(
    (info: { getValue: () => string; row: Row<ReferralTableRow> }) => {
      const row = info.row.original;
      const value = info.getValue();

      if (row.isSubRow) return <SubRowName>{value}</SubRowName>;
      if (!isSubDivisible || !info.row.getCanExpand()) return <LeafName>{value}</LeafName>;

      const isExpanded = info.row.getIsExpanded();
      return (
        <ExpandButton type='button' aria-expanded={isExpanded} onClick={() => info.row.toggleExpanded()}>
          <Chevron $expanded={isExpanded} />
          <span>{value}</span>
        </ExpandButton>
      );
    },
    [isSubDivisible]
  );

  const rowStyle = useCallback(
    (row: Row<ReferralTableRow>) => (row.original.isSubRow ? { backgroundColor: colors.black05 } : undefined),
    []
  );

  if (!slideoutDimension) return null;

  const entityHeader = getEntityColumnHeader(slideoutDimension, headerNames);

  return (
    <Wrapper>
      {(segmentedOptions.length > 1 || exportSlot) && (
        <ViewByRow>
          {segmentedOptions.length > 1 && (
            <>
              <ViewByLabel>View By:</ViewByLabel>
              <SegmentedControl
                options={segmentedOptions}
                selectedValue={slideoutDimension}
                onChange={(val) => {
                  setSlideoutDimension(val as Dimensions);
                  setExpanded({});
                }}
                mobileFullWidth
              />
            </>
          )}
          {exportSlot && <ExportSlot>{exportSlot}</ExportSlot>}
        </ViewByRow>
      )}
      <TableWrapper>
        <ReferralDetailsTable
          loading={loading}
          rollups={rollups}
          rows={tableRows}
          rowStyle={rowStyle}
          dimension={slideoutDimension}
          entityHeader={entityHeader}
          entityCell={entityCell}
          visibleColumns={visibleColumns}
          defaultSort={defaultSort}
          getSubRows={isSubDivisible ? getSubRowsAccessor : undefined}
          expanded={expanded}
          onExpandedChange={setExpanded}
          referralsHeader='Referrals'
        />
      </TableWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ViewByRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 800px) {
    flex-wrap: wrap;
  }
`;

const ExportSlot = styled.div`
  margin-left: auto;
  align-self: flex-start;
  display: flex;
  align-items: center;
`;

const ViewByLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  letter-spacing: 0.18px;

  @media (max-width: 800px) {
    display: none;
  }
`;
